<template>
  <section class="invoice-create-page">
    <div class="invoice-create-top-grid invoice-detail-top-grid">
      <SimpleNoteDetailOperationCard
        :operation="operation"
        :comments="comments"
        :total="total"
        :currency="currency"
      />

      <SimpleNoteDetailSummaryCard :note="note" />
    </div>

    <article class="invoice-create-card invoice-create-concepts-card">
      <header class="invoice-create-card__head invoice-create-card__head--space">
        <h3>{{ conceptsTitle }}</h3>
      </header>

      <div class="app-table-wrap invoice-create-concepts-table-wrap">
        <div class="app-table-scroll">
          <table class="app-data-table">
            <thead>
              <tr>
                <th>{{ conceptsHeaders.quantity }}</th>
                <th>{{ conceptsHeaders.unit }}</th>
                <th>{{ conceptsHeaders.concept }}</th>
                <th class="app-text-right">{{ conceptsHeaders.unitPrice }}</th>
                <th class="app-text-right">{{ conceptsHeaders.amount }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="normalizedConcepts.length === 0">
                <td colspan="5" class="app-table-empty">{{ conceptsEmptyText }}</td>
              </tr>

              <tr v-for="(concept, index) in normalizedConcepts" :key="`simple-note-concept-${index}`">
                <td>{{ concept.quantity }}</td>
                <td>{{ concept.unit }}</td>
                <td>
                  <router-link
                    v-if="buildConceptRoute(concept)"
                    :to="buildConceptRoute(concept)"
                    class="invoice-create-concept-description-link invoice-create-concept-description-link--detail"
                    :title="conceptSaleLinkTitle"
                  >
                    {{ concept.description }}
                  </router-link>
                  <span v-else>{{ concept.description }}</span>
                </td>
                <td class="app-text-right">{{ formatAmountWithCurrency(concept.unitPrice, currency) }}</td>
                <td class="app-text-right"><strong>{{ formatAmountWithCurrency(concept.amount, currency) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </article>

    <InvoiceCreateBottomSection
      :comments="comments"
      :discount-rate="0"
      :subtotal="subtotal"
      :discount-amount="0"
      :transferred-tax-amount="0"
      :withheld-tax-amount="0"
      :total="total"
      :currency="currency"
      :read-only="true"
      :hide-comments="true"
      :show-subtotal="false"
      :empty-read-only-comments-placeholder="''"
    >
      <template #left-card>
        <RecentActivityCard
          :entity-id="simpleNoteId"
          entity-type="simple-note"
          :per-page="4"
          :show-footer="false"
        />
      </template>
    </InvoiceCreateBottomSection>
  </section>
</template>

<script>
import InvoiceCreateBottomSection from '@/components/invoices/create/InvoiceCreateBottomSection.vue';
import RecentActivityCard from '@/components/common/RecentActivityCard.vue';
import SimpleNoteDetailOperationCard from '@/components/simple-notes/detail/tabs/SimpleNoteDetailOperationCard.vue';
import SimpleNoteDetailSummaryCard from '@/components/simple-notes/detail/tabs/SimpleNoteDetailSummaryCard.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import { formatAmountWithCurrency } from '@/utils/numberFormat';

const { catalog: simpleNotesText } = useDomainText('simpleNotes');

export default {
  name: 'SimpleNoteDetailTabDetail',
  components: {
    InvoiceCreateBottomSection,
    RecentActivityCard,
    SimpleNoteDetailOperationCard,
    SimpleNoteDetailSummaryCard,
  },
  props: {
    note: {
      type: Object,
      default: null,
    },
    operation: {
      type: Object,
      default: () => ({}),
    },
    concepts: {
      type: Array,
      default: () => [],
    },
    comments: {
      type: String,
      default: '',
    },
    currency: {
      type: String,
      default: 'MXN',
    },
    subtotal: {
      type: Number,
      default: 0,
    },
    total: {
      type: Number,
      default: 0,
    },
  },
  emits: ['pay-note'],
  data() {
    return {
      text: simpleNotesText,
    };
  },
  computed: {
    simpleNoteId() {
      return Number(this.note?.id || 0) || 0;
    },
    conceptsTitle() {
      return String(this.text?.create?.ui?.conceptsCard?.title || 'Conceptos').trim() || 'Conceptos';
    },
    conceptsHeaders() {
      return {
        quantity: 'Cantidad',
        unit: 'Unidad',
        concept: 'Concepto',
        unitPrice: 'VALOR UNITARIO',
        amount: 'Importe',
      };
    },
    conceptsEmptyText() {
      return String(this.text?.create?.ui?.conceptsCard?.table?.empty || 'Sin conceptos').trim() || 'Sin conceptos';
    },
    conceptSaleLinkTitle() {
      return String(this.text?.table?.links?.saleDetail || 'Ir al detalle del negocio').trim() || 'Ir al detalle del negocio';
    },
    linkedPaymentIds() {
      return new Set(
        (Array.isArray(this.note?.payments) ? this.note.payments : [])
          .map((payment) => Number(payment?.id || 0) || 0)
          .filter((id) => id > 0)
      );
    },
    normalizedConcepts() {
      return (Array.isArray(this.concepts) ? this.concepts : []).map((concept) => {
        const quantity = Number(concept?.quantity || 0) || 0;
        const unitPrice = Number(concept?.price || 0) || 0;
        const paymentId = Number(concept?.payment_id || 0) || 0;
        const saleId = Number(concept?.sale_id || 0) || 0;
        const hasLinkedPayment = paymentId > 0 && this.linkedPaymentIds.has(paymentId);

        return {
          quantity,
          unit: String(concept?.unit || '').trim() || '-',
          description: String(concept?.description || '').trim() || '-',
          unitPrice,
          amount: Number(concept?.import || (quantity * unitPrice) || 0) || 0,
          saleId,
          hasLinkedPayment,
        };
      });
    },
  },
  methods: {
    formatAmountWithCurrency,
    buildConceptRoute(concept) {
      const saleId = Number(concept?.saleId || concept?.sale_id || 0) || 0;
      const hasLinkedPayment = concept?.hasLinkedPayment === true;

      if (saleId <= 0 || !hasLinkedPayment) {
        return null;
      }

      return {
        name: 'saleDetail',
        params: { saleId: String(saleId) },
      };
    },
  },
};
</script>

<style scoped>
.invoice-detail-top-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

@media (max-width: 1100px) {
  .invoice-detail-top-grid {
    grid-template-columns: 1fr;
  }
}
</style>
